import { toast } from 'sonner';

/**
 * Validates form data and line items against provided rules
 * @param {Object} params - Parameters object
 * @param {Object} params.data - Form data to validate
 * @param {Array} [params.items=[]] - Line items to validate (optional)
 * @param {Object} params.rules - Validation rules configuration
 * @returns {Object} Object containing field error messages
 */
export const validateForm = ({ data = {}, items = [], rules = {} }) => {
    const errors = {};

    // Early return if no rules provided
    if (!rules || typeof rules !== 'object') {
        console.warn('validateForm: No validation rules provided');
        return errors;
    }

    // 🔹 Validate form fields
    Object.keys(rules).forEach((field) => {
        // Skip internal _items property during form field validation
        if (field === '_items') return;

        const rule = rules[field];

        // Ensure rule exists and is an object
        if (!rule || typeof rule !== 'object') return;

        if (rule.required) {
            const value = data?.[field];
            const isInvalid =
                value === undefined ||
                value === null ||
                (typeof value === 'string' && value.trim() === '') ||
                (Array.isArray(value) && value.length === 0) ||
                (typeof value === 'number' && (isNaN(value) || value === 0));

            if (isInvalid) {
                errors[field] = rule?.message || `${rule?.label} is required`;
            }
        }
    });

    // 🔹 Validate line items (if any)
    if (items.length > 0 && rules._items && typeof rules._items === 'object') {
        const itemRules = rules._items;

        items.forEach((item, index) => {
            Object.keys(itemRules).forEach((field) => {
                if (field === '_items') return;

                const rule = itemRules[field];
                if (!rule || typeof rule !== 'object') return;

                if (rule.required) {
                    const value = item[field];
                    const isInvalid =
                        value === undefined ||
                        value === null ||
                        (typeof value === 'string' && value.trim() === '') ||
                        (typeof value === 'number' && (isNaN(value) || value <= 0));

                    if (isInvalid) {
                        errors[`${field}_${index}`] =
                            `${rule.message} is required in row ${index + 1}` || `${field} is required for item ${index + 1}`;
                    }
                }
            });
        });
    }

    // 🔥 Show all errors via toast
    const errorMessages = Object.values(errors).map((errFn, idx) => {
        // Check if it's a function and call it with index if needed
        return typeof errFn === 'function' ? errFn(idx) : errFn;
    });

    if (errorMessages.length > 0) {
        toast.error(
            <div>
                <strong>Please fix the following errors:</strong>
                <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                    {errorMessages.map((msg, idx) => (
                        <li key={idx}>{msg}</li>
                    ))}
                </ul>
            </div>,
            {
                duration: 5000,
                closeButton: true,
            }
        );
    }

    return errors;
};